import { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Mail, MapPin, Phone, Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { FieldError } from "./FieldError";
import { validators } from "../../lib/validation";

export const Footer = ({ categories = [] }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const msg = validators.email(email);
    setError(msg);
    if (msg) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email: email.trim().toLowerCase() });
      toast.success("Subscribed! Welcome to the BookMala readers circle.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe right now. Please try again.");
    }
    setBusy(false);
  };
  return (
    <footer className="bg-[#1A1A1A] text-[#D1D1D1]" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 lg:py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-2.5 mb-5">
            <BookOpen className="w-6 h-6 text-bm-sand" />
            <span className="font-display text-2xl font-bold text-white">BookMala</span>
          </div>
          <p className="text-sm leading-relaxed mb-6 text-[#B0B0B0]">
            India's warmest magazine subscription store. Print and digital editions from the country's most loved publishing houses.
          </p>
          <form onSubmit={submit} data-testid="newsletter-form" noValidate>
            <div className="flex gap-2">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                onBlur={() => email && setError(validators.email(email))}
                placeholder="Your email address" data-testid="newsletter-email-input" aria-invalid={!!error}
                className={`flex-1 bg-[#2A2A2A] border rounded-lg px-4 py-2.5 text-sm placeholder-[#757575] focus:outline-none transition-colors duration-200 ${error ? "border-red-400" : "border-[#3A3A3A] focus:border-bm-sand"}`} />
              <button type="submit" disabled={busy} data-testid="newsletter-submit-btn"
                className="bg-bm-terra text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-bm-terra-dark transition-colors duration-200 disabled:opacity-50">
                {busy ? "..." : "Join"}
              </button>
            </div>
            <FieldError msg={error} id="newsletter-email" />
          </form>
          <div className="flex gap-3 mt-6" data-testid="social-links">
            {[Facebook, Instagram, Twitter, Youtube].map((Icon, i) => (
              <a key={i} href="https://example.com" aria-label="Social link" data-testid={`social-link-${i}`}
                className="w-9 h-9 rounded-full border border-[#3A3A3A] flex items-center justify-center text-[#B0B0B0] hover:text-bm-sand hover:border-bm-sand transition-colors duration-200">
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <h4 className="font-display font-semibold mb-4 text-white">Explore</h4>
          <ul className="space-y-2.5 text-sm text-[#B0B0B0]">
            <li><Link to="/magazines" className="hover:text-bm-sand transition-colors duration-200">All Magazines</Link></li>
            <li><Link to="/publishers" className="hover:text-bm-sand transition-colors duration-200">Publishers</Link></li>
            <li><Link to="/pricing" className="hover:text-bm-sand transition-colors duration-200">Subscription Plans</Link></li>
            <li><Link to="/track" className="hover:text-bm-sand transition-colors duration-200">Track Your Order</Link></li>
            <li><Link to="/blog" className="hover:text-bm-sand transition-colors duration-200">Journal</Link></li>
          </ul>
        </div>
        <div className="md:col-span-3">
          <h4 className="font-display font-semibold mb-4 text-white">Categories</h4>
          <ul className="space-y-2.5 text-sm text-[#B0B0B0]">
            {categories.slice(0, 6).map((c) => (
              <li key={c.id || c.slug}>
                <Link to={`/magazines?category=${c.slug}`} className="hover:text-bm-sand transition-colors duration-200">{c.name}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3">
          <h4 className="font-display font-semibold mb-4 text-white">Company</h4>
          <ul className="space-y-2.5 text-sm text-[#B0B0B0] mb-6">
            <li><Link to="/about" className="hover:text-bm-sand transition-colors duration-200">About BookMala</Link></li>
            <li><Link to="/contact" className="hover:text-bm-sand transition-colors duration-200">Contact Us</Link></li>
            <li><a href="/api/sitemap.xml" className="hover:text-bm-sand transition-colors duration-200">Sitemap</a></li>
          </ul>
          <ul className="space-y-2.5 text-sm text-[#B0B0B0]">
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-bm-sand" /> care@bookmala.in</li>
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-bm-sand" /> +91 98765 00000</li>
            <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-bm-sand" /> Connaught Place, New Delhi</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[#2A2A2A] py-6 text-center text-xs text-[#757575]">
        © {new Date().getFullYear()} BookMala. All rights reserved. Secure payments · Easy cancellation · Pan-India delivery.
      </div>
    </footer>
  );
};
